'use client'

import { useCallback, useMemo, useRef } from 'react'

import { useInfiniteQuery, useQuery } from '@tanstack/react-query'

import { useAuthInfo } from '@sweetauth/core'

import {
  type ApiIdvActivityResponse,
  type ApiIdvChildAggregation,
  type ApiOrderItem,
  fetchAward,
  fetchAwardOrders,
  fetchAwardSubawards,
  fetchAwardTransactions,
  fetchIdvActivity,
  fetchIdvChildAggregation,
} from '@/context/usaspending'

import type { Award, SubAward, Transaction } from '@/types/usaspending'

import { usaspendingKeys } from './queryKeys'
import { assertPageNumber, assertToken } from './utils'

// ============================================================================
// Types for paginated data
// ============================================================================

interface PaginatedPage<T> {
  data: T[]
  total: number
  hasMore: boolean
}

/**
 * Derived data from paginated infinite query
 * Separates items (flattened) from total count (from API)
 */
export interface PaginatedResult<T> {
  /** Flattened items from all loaded pages */
  items: T[]
  /** Total count from API (not loaded count) */
  total: number
}

/**
 * Hook to fetch award details
 *
 * @param id - The award ID
 * @param options.enabled - Whether to enable the query (default: true)
 */
export function useAward(id: string, options: { enabled?: boolean } = {}) {
  const { enabled = true } = options
  const { accessToken } = useAuthInfo()

  return useQuery<Award>({
    queryKey: usaspendingKeys.awardDetail(id),
    queryFn: () => fetchAward(assertToken(accessToken), id),
    enabled: enabled && !!id && !!accessToken,
    staleTime: 30 * 60 * 1000, // 30 minutes - award data rarely changes
    gcTime: 60 * 60 * 1000, // 1 hour
    refetchOnWindowFocus: false,
    refetchOnMount: false,
    // Don't retry on 404 - solicitations without awards are expected
    retry: (failureCount, error) => {
      if (error instanceof Error && error.message.includes('404')) {
        return false
      }
      return failureCount < 3
    },
  })
}

/**
 * Hook to fetch award transactions with pagination
 *
 * Returns both query controls (fetchNextPage, hasNextPage, etc.) and
 * memoized derived data (items, total) for compositional consumption.
 *
 * @param awardId - The award ID
 * @param options.enabled - Whether to enable the query (default: true)
 * @param options.limit - Items per page (default: 25)
 */
export function useAwardTransactions(
  awardId: string,
  options: { enabled?: boolean; limit?: number } = {},
) {
  const { enabled = true, limit = 50 } = options
  const { accessToken } = useAuthInfo()
  const isNumericAwardId = /^\d+$/.test(awardId)

  const query = useInfiniteQuery<PaginatedPage<Transaction>>({
    queryKey: usaspendingKeys.awardTransactions(awardId),
    queryFn: ({ pageParam = 1 }) =>
      fetchAwardTransactions(
        assertToken(accessToken),
        awardId,
        assertPageNumber(pageParam),
        limit,
      ),
    enabled: enabled && !!awardId && !!accessToken && isNumericAwardId,
    staleTime: 30 * 60 * 1000, // 30 minutes - transaction data rarely changes
    gcTime: 60 * 60 * 1000, // 1 hour
    refetchOnWindowFocus: false,
    refetchOnMount: false,
    initialPageParam: 1,
    getNextPageParam: (lastPage, allPages) =>
      lastPage.hasMore ? allPages.length + 1 : undefined,
  })

  // Memoized derived data - items flattened from pages, total from API
  const derived = useMemo((): PaginatedResult<Transaction> => {
    if (!query.data) return { items: [], total: 0 }
    return {
      items: query.data.pages.flatMap((page) => page.data),
      total: query.data.pages[0]?.total ?? 0,
    }
  }, [query.data])

  // Track if we're currently fetching all pages
  const isFetchingAllRef = useRef(false)

  // Fetch all remaining pages (for sort/filter operations)
  const fetchAllPages = useCallback(async () => {
    if (
      isFetchingAllRef.current ||
      query.isFetchingNextPage ||
      !query.hasNextPage
    ) {
      return
    }

    isFetchingAllRef.current = true
    try {
      // Keep fetching until no more pages
      let canFetchMore = true
      while (canFetchMore) {
        const result = await query.fetchNextPage()
        const lastPage = result.data?.pages[result.data.pages.length - 1]
        canFetchMore = lastPage?.hasMore ?? false
      }
    } finally {
      isFetchingAllRef.current = false
    }
  }, [query])

  // Whether all data has been loaded
  const isFullyLoaded =
    !query.hasNextPage && derived.items.length >= derived.total

  return {
    ...query,
    // Derived data (memoized)
    items: derived.items,
    total: derived.total,
    // Eager loading support
    fetchAllPages,
    isFullyLoaded,
  }
}

/**
 * Hook to fetch award subawards with pagination
 *
 * Returns both query controls (fetchNextPage, hasNextPage, etc.) and
 * memoized derived data (items, total) for compositional consumption.
 *
 * @param awardId - The award ID
 * @param options.enabled - Whether to enable the query (default: true)
 * @param options.limit - Items per page (default: 25)
 */
export function useAwardSubawards(
  awardId: string,
  options: { enabled?: boolean; limit?: number } = {},
) {
  const { enabled = true, limit = 50 } = options
  const { accessToken } = useAuthInfo()

  const query = useInfiniteQuery<PaginatedPage<SubAward>>({
    queryKey: usaspendingKeys.awardSubawards(awardId),
    queryFn: ({ pageParam = 1 }) =>
      fetchAwardSubawards(
        assertToken(accessToken),
        awardId,
        assertPageNumber(pageParam),
        limit,
      ),
    enabled: enabled && !!awardId && !!accessToken,
    staleTime: 30 * 60 * 1000, // 30 minutes - subaward data rarely changes
    gcTime: 60 * 60 * 1000, // 1 hour
    refetchOnWindowFocus: false,
    refetchOnMount: false,
    initialPageParam: 1,
    getNextPageParam: (lastPage, allPages) =>
      lastPage.hasMore ? allPages.length + 1 : undefined,
  })

  // Memoized derived data - items flattened from pages, total from API
  const derived = useMemo((): PaginatedResult<SubAward> => {
    if (!query.data) return { items: [], total: 0 }
    return {
      items: query.data.pages.flatMap((page) => page.data),
      total: query.data.pages[0]?.total ?? 0,
    }
  }, [query.data])

  // Track if we're currently fetching all pages
  const isFetchingAllRef = useRef(false)

  // Fetch all remaining pages (for sort/filter operations)
  const fetchAllPages = useCallback(async () => {
    if (
      isFetchingAllRef.current ||
      query.isFetchingNextPage ||
      !query.hasNextPage
    ) {
      return
    }

    isFetchingAllRef.current = true
    try {
      // Keep fetching until no more pages
      let canFetchMore = true
      while (canFetchMore) {
        const result = await query.fetchNextPage()
        const lastPage = result.data?.pages[result.data.pages.length - 1]
        canFetchMore = lastPage?.hasMore ?? false
      }
    } finally {
      isFetchingAllRef.current = false
    }
  }, [query])

  // Whether all data has been loaded
  const isFullyLoaded =
    !query.hasNextPage && derived.items.length >= derived.total

  return {
    ...query,
    // Derived data (memoized)
    items: derived.items,
    total: derived.total,
    // Eager loading support
    fetchAllPages,
    isFullyLoaded,
  }
}

/**
 * Hook to fetch child awards (orders) for an IDV with pagination
 *
 * Orders are task/delivery orders placed under an IDV (master contract).
 * Returns both query controls (fetchNextPage, hasNextPage, etc.) and
 * memoized derived data (items, total) for compositional consumption.
 *
 * @param awardId - The IDV award ID
 * @param options.enabled - Whether to enable the query (default: true)
 * @param options.limit - Items per page (default: 25)
 */
export function useAwardOrders(
  awardId: string,
  options: { enabled?: boolean; limit?: number } = {},
) {
  const { enabled = true, limit = 50 } = options
  const { accessToken } = useAuthInfo()

  const query = useInfiniteQuery<PaginatedPage<ApiOrderItem>>({
    queryKey: usaspendingKeys.awardOrders(awardId),
    queryFn: ({ pageParam = 1 }) =>
      fetchAwardOrders(
        assertToken(accessToken),
        awardId,
        assertPageNumber(pageParam),
        limit,
      ),
    enabled: enabled && !!awardId && !!accessToken,
    staleTime: 30 * 60 * 1000, // 30 minutes - order data rarely changes
    gcTime: 60 * 60 * 1000, // 1 hour
    refetchOnWindowFocus: false,
    refetchOnMount: false,
    initialPageParam: 1,
    getNextPageParam: (lastPage, allPages) =>
      lastPage.hasMore ? allPages.length + 1 : undefined,
  })

  // Memoized derived data - items flattened from pages, total from API
  const derived = useMemo((): PaginatedResult<ApiOrderItem> => {
    if (!query.data) return { items: [], total: 0 }
    return {
      items: query.data.pages.flatMap((page) => page.data),
      total: query.data.pages[0]?.total ?? 0,
    }
  }, [query.data])

  // Track if we're currently fetching all pages
  const isFetchingAllRef = useRef(false)

  // Fetch all remaining pages (for sort/filter operations)
  const fetchAllPages = useCallback(async () => {
    if (
      isFetchingAllRef.current ||
      query.isFetchingNextPage ||
      !query.hasNextPage
    ) {
      return
    }

    isFetchingAllRef.current = true
    try {
      // Keep fetching until no more pages
      let canFetchMore = true
      while (canFetchMore) {
        const result = await query.fetchNextPage()
        const lastPage = result.data?.pages[result.data.pages.length - 1]
        canFetchMore = lastPage?.hasMore ?? false
      }
    } finally {
      isFetchingAllRef.current = false
    }
  }, [query])

  // Whether all data has been loaded
  const isFullyLoaded =
    !query.hasNextPage && derived.items.length >= derived.total

  return {
    ...query,
    // Derived data (memoized)
    items: derived.items,
    total: derived.total,
    // Eager loading support
    fetchAllPages,
    isFullyLoaded,
  }
}

/**
 * Hook to fetch aggregated financials from IDV child orders
 *
 * Returns the sum of obligations and outlays from all child orders under an IDV.
 * This enables meaningful progress display for IDV awards where obligations
 * occur at the child order level rather than the master contract level.
 *
 * @param awardId - The IDV award ID
 * @param options.enabled - Whether to enable the query (default: true)
 */
export function useIdvChildAggregation(
  awardId: string,
  options: { enabled?: boolean } = {},
) {
  const { enabled = true } = options
  const { accessToken } = useAuthInfo()

  return useQuery<ApiIdvChildAggregation>({
    queryKey: usaspendingKeys.awardOrdersAggregation(awardId),
    queryFn: () => fetchIdvChildAggregation(assertToken(accessToken), awardId),
    enabled: enabled && !!awardId && !!accessToken,
    staleTime: 30 * 60 * 1000, // 30 minutes - aggregation data rarely changes
    gcTime: 60 * 60 * 1000, // 1 hour
    refetchOnWindowFocus: false,
    refetchOnMount: false,
  })
}

/**
 * Hook to fetch IDV activity hierarchy for Sankey visualization
 *
 * Returns parent IDV, child awards, and grandchild awards in a format
 * suitable for @nivo/sankey visualization showing money flow.
 *
 * @param awardId - The IDV award ID
 * @param options.enabled - Whether to enable the query (default: true)
 */
export function useIdvActivity(
  awardId: string,
  options: { enabled?: boolean } = {},
) {
  const { enabled = true } = options
  const { accessToken } = useAuthInfo()

  return useQuery<ApiIdvActivityResponse>({
    queryKey: usaspendingKeys.awardActivity(awardId),
    queryFn: () => fetchIdvActivity(assertToken(accessToken), awardId),
    enabled: enabled && !!awardId && !!accessToken,
    staleTime: 30 * 60 * 1000, // 30 minutes - activity data rarely changes
    gcTime: 60 * 60 * 1000, // 1 hour
    refetchOnWindowFocus: false,
    refetchOnMount: false,
  })
}
